"use client";
import {
  Boxes,
  Braces,
  Download,
  FileArchive,
  FileDown,
  FileJson,
  FileText,
  LoaderCircle,
} from "lucide-react";
import JSZip from "jszip";
import {
  useState,
} from "react";
import {
  reportToCsv,
  reportToMarkdown,
  reportToSarif,
} from "../../../lib/report-export";
import {
  CYCLONEDX_SPEC_VERSION,
  reportToCycloneDx,
  sbomExportDetail,
} from "../../../lib/sbom";
import {
  type ScanReport,
} from "../../../lib/types";
import {
  downloadBlob,
} from "../../shared/io";


export function ReportsView({
  report,
  onToast,
}: {
  report: ScanReport;
  onToast: (message: string) => void;
}) {
  const [bundling, setBundling] = useState(false);
  const baseName = `${report.id}-jarlab-report`;
  const exports = [
    {
      label: "JSON",
      detail: "Complete machine-readable analysis",
      extension: "json",
      type: "application/json",
      build: () => JSON.stringify(report, null, 2),
      icon: FileJson,
    },
    {
      label: "Markdown",
      detail: "Readable findings and behavior-chain report",
      extension: "md",
      type: "text/markdown",
      build: () => reportToMarkdown(report),
      icon: FileText,
    },
    {
      label: "CSV",
      detail: "Flat findings, actions, and indicator table",
      extension: "csv",
      type: "text/csv",
      build: () => reportToCsv(report),
      icon: FileDown,
    },
    {
      label: "SARIF 2.1.0",
      detail: "Compatible with code-scanning and CI tools",
      extension: "sarif",
      type: "application/sarif+json",
      build: () => reportToSarif(report),
      icon: Braces,
    },
    {
      label: `CycloneDX ${CYCLONEDX_SPEC_VERSION} SBOM`,
      detail: sbomExportDetail(report),
      extension: "cdx.json",
      type: "application/vnd.cyclonedx+json",
      build: () => reportToCycloneDx(report),
      icon: Boxes,
    },
  ];

  const downloadAll = async () => {
    if (bundling) return;
    setBundling(true);
    try {
      const archive = new JSZip();
      for (const item of exports) {
        archive.file(`${baseName}.${item.extension}`, item.build());
      }
      archive.file(
        "README.md",
        [
          "# JarLab report bundle",
          "",
          `Sample: ${report.fileName}`,
          `SHA-256: ${report.hashes.sha256}`,
          `Verdict: ${report.verdict} (${report.score}/100)`,
          "",
          "The bundle contains the same static-analysis result in JSON, Markdown, CSV, SARIF, and CycloneDX SBOM formats.",
          "",
          "The SBOM lists only Maven coordinates JarLab read from coordinate files inside the artifact. It carries no dependency scopes and no transitive edges, because static analysis did not observe either.",
          "",
        ].join("\n"),
      );
      const output = await archive.generateAsync({
        type: "uint8array",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });
      downloadBlob(output, `${baseName}-bundle.zip`, "application/zip");
      onToast("Report bundle downloaded");
    } finally {
      setBundling(false);
    }
  };

  return (
    <div className="view-stack">
      <section className="panel">
        <div className="panel-heading">
          <div>
            <div className="section-kicker">EXPORT CENTER</div>
            <h3>Use the report wherever you work</h3>
          </div>
          <span>{report.findings.length} findings</span>
        </div>
        <div className="export-format-grid">
          {exports.map((item) => {
            const Icon = item.icon;
            return (
              <article key={item.label}>
                <Icon size={20} />
                <div>
                  <strong>{item.label}</strong>
                  <span>{item.detail}</span>
                </div>
                <button
                  className="secondary-button compact"
                  type="button"
                  onClick={() => {
                    downloadBlob(
                      item.build(),
                      `${baseName}.${item.extension}`,
                      item.type,
                    );
                    onToast(`${item.label} report downloaded`);
                  }}
                >
                  <Download size={14} />
                  Download
                </button>
              </article>
            );
          })}
        </div>
        <div className="export-bundle-row">
          <div>
            <strong>Complete report bundle</strong>
            <span>
              All {exports.length} formats in one ZIP with a short manifest
            </span>
          </div>
          <button
            className="primary-button"
            type="button"
            disabled={bundling}
            onClick={() => void downloadAll()}
          >
            {bundling ? <LoaderCircle size={15} /> : <FileArchive size={15} />}
            {bundling ? "Building…" : "Download bundle"}
          </button>
        </div>
      </section>
    </div>
  );
}
